import { useCallback, useRef, useState } from "react";

import { ApiError } from "./httpClient";

export interface SaveState {
  readonly saving: boolean;
  readonly message: string | null;
  readonly tone: "ok" | "error" | "info";
}

const IDLE: SaveState = { saving: false, message: null, tone: "info" };

/**
 * Wraps a save call with its own pending, success and failure reporting.
 *
 * A 401 means the session expired mid-edit; that is escalated to the page so it
 * can show the sign-in form instead of pretending the save merely failed.
 *
 * The in-flight ref is the part worth keeping: without it, a double-click on a
 * submit button sends the request twice. Shared by the admin panels and the
 * member-facing forms, which is why it lives here rather than under admin/.
 */
export function useSaveState(onUnauthenticated?: () => void) {
  const [state, setState] = useState<SaveState>(IDLE);
  const inFlight = useRef(false);

  const run = useCallback(
    async <T,>(action: () => Promise<T>, successMessage?: (result: T) => string) => {
      if (inFlight.current) return undefined;
      inFlight.current = true;
      setState({ saving: true, message: null, tone: "info" });

      try {
        const result = await action();
        setState({
          saving: false,
          tone: "ok",
          message: successMessage ? successMessage(result) : "Saved.",
        });
        return result;
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthenticated) {
          onUnauthenticated?.();
        }
        setState({
          saving: false,
          tone: "error",
          message: error instanceof Error ? error.message : "Something went wrong.",
        });
        return undefined;
      } finally {
        inFlight.current = false;
      }
    },
    [onUnauthenticated],
  );

  const reset = useCallback(() => setState(IDLE), []);

  return { ...state, run, reset } as const;
}
